import _ from 'lodash'
import { FusionError, serializeFusionError, UserIdentity } from '@fusion-advantage/backcompat'
import { DashboardLoadStatus, DashboardNotification, getDisplayIdentity, StatusedAppointment, StatusedTodo, SyncResultStatus, WorkItemType } from "@fusion-advantage/formlib"
import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { AppRootState, AppThunk } from "./appStore"
import { appError } from "./errorSlice"
import { selectSyncResult } from "./syncSlice"
import { appStatusBodyToUserIdentities } from './syncthunk/ZCloudToDevice'
import { err, ok, Result } from 'neverthrow'
import { setAppEnvironment, setSessionExpiredAt } from './appSlice'
import { selectAppStatus } from '../dal2/selectAppStatus'
import { WorkItemSelector } from '../dal2/WorkItemSelector'
import { selectDeviceToCloudBlobSyncItemsCount } from '../dal/ldbDeviceToCloudBlob'
import { selectAllDashboardNotifications } from '../dal/ldbDashboardNotifications'
import { selectIsPreviewMode } from './selectPreview'

// the purpose of this slice is to hold read-only data pulled in from the
// local database only—  user interaction state belongs in the other slice

type WorkItemState<T extends StatusedAppointment|StatusedTodo> = {
	status: DashboardLoadStatus.Loading
	items?: never
} | {
	status: DashboardLoadStatus.Error
	items?: never
	error: FusionError
} | {
	status: DashboardLoadStatus.Ready
	items: T[]
}

export interface DashboardDataSliceState {
	authedIdentity: UserIdentity|null
	impersonatingIdentity: UserIdentity|null
	filterByDate: boolean
	apptsStartDate: string|null
	apptsEndDate: string|null
	workItems: {
		[WorkItemType.Todo]: WorkItemState<StatusedTodo>
		[WorkItemType.Appointment]: WorkItemState<StatusedAppointment>
	}
	pendingDeviceToCloudCount: number
	dashboardNotifications: DashboardNotification[]
}

const initialState: DashboardDataSliceState = {
	authedIdentity: null,
	impersonatingIdentity: null,
	filterByDate: true,
	apptsStartDate: null,
	apptsEndDate: null,
	workItems: {
		[WorkItemType.Todo]: {
			status: DashboardLoadStatus.Loading,
		},
		[WorkItemType.Appointment]: {
			status: DashboardLoadStatus.Loading,
		},
	},
	pendingDeviceToCloudCount: 0,
	dashboardNotifications: [],
}

const dashboardDataSlice = createSlice({
	name: 'dashboardData',
	initialState,
	reducers: {
		internalUpdateFromLdb(_state, action: PayloadAction<DashboardDataSliceState>) {
			return action.payload
		},
	},
})

export default dashboardDataSlice.reducer

export const selectDashboardDisplayIdentity = (state: AppRootState) => {
	return getDisplayIdentity(state.dashboardData)
}

// TODO
// it seems like here we should really pay attention to how we are
// suppressing, displaying error messages to the user
export const dashboardDataReloadFromLdb = (): AppThunk<Promise<void>> => async (dispatch, getState) => {
	const { internalUpdateFromLdb } = dashboardDataSlice.actions
	const rSync = selectSyncResult(getState())
	if(rSync.isErr() && rSync.error.status === SyncResultStatus.NoAuth) {
		// the token is not valid;
		// user must reauthenticate to continue
		dispatch(internalUpdateFromLdb(initialState))
		return
	}
	const newState = _.cloneDeep(initialState)
	// load appStatus from ldb
	const rStatus = await (async (): Promise<Result<{user: UserIdentity|null}, FusionError|string>> => {
		const rSelect = await selectAppStatus()
		if(rSelect.isErr()) {
			return err(rSelect.error)
		}
		if(!rSelect.value) {
			// if the user has not logged in yet,
			// this is not an error
			return ok({user: null})
		}
		const identities = appStatusBodyToUserIdentities(rSelect.value)
		newState.authedIdentity = identities.authedIdentity
		newState.impersonatingIdentity = identities.impersonatingIdentity
		let sessionExpiredAt = rSelect.value.SessionExpiredAt
		// convert unix time to unix milliseconds for js
		if(sessionExpiredAt) {
			sessionExpiredAt *= 1000
		}
		dispatch(setSessionExpiredAt(sessionExpiredAt))
		dispatch(setAppEnvironment(rSelect.value.Environment))
		const user = getDisplayIdentity(newState)
		if(!user) {
			return err('user expected but null')
		}
		return ok({user})
	})()
	if(rStatus.isErr()) {
		// can't do much without appStatus
		dispatch(appError({
			err: serializeFusionError('dashboardDataReloadFromLdb', rStatus.error)
		}))
		dispatch(internalUpdateFromLdb(newState))
		return
	}
	const { user } = rStatus.value
	if(!user) {
		dispatch(internalUpdateFromLdb(newState))
		return
	}
	const dashboardNotifications = await selectAllDashboardNotifications()
	if(dashboardNotifications.isErr()) {
		dispatch(appError({
			err: serializeFusionError('dashboardDataReloadFromLdb', dashboardNotifications.error),
			suppressAlert: true,
		}))
	} else {
		newState.dashboardNotifications = dashboardNotifications.value
	}
	const rPendingUploadCount = await selectDeviceToCloudBlobSyncItemsCount()
	if(rPendingUploadCount.isErr()) {
		dispatch(appError({
			err: serializeFusionError('dashboardDataReloadFromLdb', rPendingUploadCount.error),
			suppressAlert: true,
		}))
		// this is an unexpected case,
		// make the indicator turn red, at least
		newState.pendingDeviceToCloudCount = 99
	} else {
		newState.pendingDeviceToCloudCount = rPendingUploadCount.value
	}
	if(selectIsPreviewMode(getState())) {
		// preview syncs skip work item downloads, so the blobs are absent by design
		dispatch(internalUpdateFromLdb(newState))
		return
	}
	const rWorkItemSelector = await WorkItemSelector.create(user.upn)
	if(rWorkItemSelector.isErr()) {
		dispatch(appError({
			err: serializeFusionError('dashboardDataReloadFromLdb', rWorkItemSelector.error)
		}))
		dispatch(internalUpdateFromLdb(newState))
		return
	}
	// load todos from ldb
	let newTodo: WorkItemState<StatusedTodo>
	const rTodo = await rWorkItemSelector.value.selectTodos()
	if(rTodo.isErr()) {
		dispatch(appError({
			err: serializeFusionError('dashboardDataReloadFromLdb', rTodo.error),
			suppressAlert: true,
		}))
		newTodo = {
			status: DashboardLoadStatus.Error,
			error: rTodo.error,
		}
	} else {
		newTodo = {
			status: DashboardLoadStatus.Ready,
			items: rTodo.value.WorkItems,
		}
	}
	// load appts from ldb
	let newAppt: WorkItemState<StatusedAppointment>
	const rAppt = await rWorkItemSelector.value.selectAppts()
	if(rAppt.isErr()) {
		dispatch(appError({
			err: serializeFusionError('dashboardDataReloadFromLdb', rAppt.error),
			suppressAlert: true,
		}))
		newAppt = {
			status: DashboardLoadStatus.Error,
			error: rAppt.error,
		}
	} else {
		newAppt = {
			status: DashboardLoadStatus.Ready,
			items: rAppt.value.Appointments,
		}
		newState.filterByDate = rAppt.value.FilterByDate
		newState.apptsStartDate = rAppt.value.StartDate
		newState.apptsEndDate = rAppt.value.EndDate
	}
	newState.workItems[WorkItemType.Todo] = newTodo
	newState.workItems[WorkItemType.Appointment] = newAppt
	dispatch(internalUpdateFromLdb(newState))
}
